import React from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../components/Layout.jsx';

const FILTERS = [
  { key: 'open', label: 'Open' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' },
];

function StatusBadge({ status }) {
  const styles = status === 'open'
    ? 'bg-green-50 text-green-700 border-green-200'
    : 'bg-gray-50 text-gray-600 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${styles}`}>
      {status === 'open' ? 'Open' : 'Closed'}
    </span>
  );
}

export default function SupportIndex({ tickets = [], filters = {}, counts = { open: 0, closed: 0 } }) {
  const active = filters.status || 'open';

  const setFilter = (key) => {
    router.get('/support', key === 'open' ? {} : { status: key }, {
      preserveState: true,
      preserveScroll: true,
    });
  };

  const total = tickets.length;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Support</h1>
            <p className="mt-1 text-sm text-gray-500">Submit a request and our team will get back to you. Replies will show up here.</p>
          </div>
          <Link
            href="/support/new"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
            </svg>
            New request
          </Link>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {FILTERS.map((f) => {
              const isActive = active === f.key;
              const count = f.key === 'open' ? counts.open : f.key === 'closed' ? counts.closed : counts.open + counts.closed;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-semibold rounded-lg border transition cursor-pointer ${
                    isActive
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {f.label}
                  <span className={`inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 text-xs rounded-full ${
                    isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {total === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-12 h-12 bg-gray-50 text-gray-400 rounded-2xl flex items-center justify-center mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-gray-600">
                {active === 'closed' ? 'No closed tickets.' : active === 'all' ? 'No tickets yet.' : 'No open tickets.'}
              </p>
              <Link
                href="/support/new"
                className="mt-3 inline-flex text-sm font-semibold text-indigo-600 hover:text-indigo-700"
              >
                Start a new request →
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {tickets.map((t) => (
                <Link
                  key={t.id}
                  href={`/support/${t.id}`}
                  className="block py-4 first:pt-0 last:pb-0 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900 truncate">{t.subject}</p>
                        <StatusBadge status={t.status} />
                        {t.last_message_from_staff && t.status === 'open' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-indigo-50 text-indigo-700 border-indigo-200">
                            New reply
                          </span>
                        )}
                      </div>
                      {t.last_message_preview && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{t.last_message_preview}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1.5">
                        {t.message_count} {t.message_count === 1 ? 'message' : 'messages'}
                        {t.last_activity_label && <span> · {t.last_activity_label}</span>}
                      </p>
                    </div>
                    <svg className="w-5 h-5 text-gray-300 shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
